import React from 'react';

import { Subscriber } from '@atlaskit/rovo-triggers';

import { type FlagsStateContainer, withFlags } from '@confluence/flags';
import { useLoomEntryPointErrorCallback } from '@confluence/loom-utils/entry-points/useLoomEntryPointErrorCallback';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { useLoomCrossJoinUrl } from '@confluence/loom-utils/entry-points/useLoomCrossJoinUrl';

import { useHandleOpenLoomRecorder } from './hooks/useHandleOpenLoomRecorder';

interface RovoChatLoomScriptCtaListenerProps {
	flags: FlagsStateContainer;
	/**
	 * Content Id of the page that we are currently listening on
	 */
	contentId: string;
}

/**
 * A React component that listens for Rovo chat CTA events and handles Loom integration.
 * Subscribes to "ai-mate" events and opens Loom recorder or cross-join URL based on the use case.
 *
 * Makes use of {@link Subscriber} from `@atlaskit/rovo-triggers`
 *
 * @component
 * @example
 * // Basic usage - the component is wrapped with withFlags HOC
 * import { RovoChatLoomScriptCtaListener } from './RovoChatLoomScriptCtaListener';
 *
 * return (
 *   <div>
 *     <RovoChatLoomScriptCtaListener />
 *   </div>
 * );
 *
 * @returns A Subscriber component that listens for chat CTA events
 */
export const RovoChatLoomScriptCtaListener = withFlags(
	({ flags, contentId }: RovoChatLoomScriptCtaListenerProps) => {
		const { error, entryPointVariant } = useLoomEntryPointVariant();
		const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });
		const crossJoinUrl = useLoomCrossJoinUrl();

		const openLoomRecorder = useHandleOpenLoomRecorder(contentId, flags);

		return (
			<Subscriber
				topic="ai-mate"
				triggerLatest
				flushQueueOnUnmount
				onEvent={(payload) => {
					if (error) {
						loomEntryPointErrorCallback();
						return;
					}
					if (payload.type === 'chat-cta-clicked') {
						switch (payload.data.useCaseId) {
							case 'loom-confluence-page-script-co-use':
								if (entryPointVariant === 'CO_USE') void openLoomRecorder();
								break;
							case 'loom-confluence-page-script-cross-join':
								if (entryPointVariant === 'CROSS_JOIN') window.open(crossJoinUrl, '_blank');
								break;
						}
					}
				}}
			/>
		);
	},
);
